import { useState } from "react";
import { SignIn, useAuth, useUser } from "@clerk/react";
import { apiFetch } from "@/lib/api";

export function validMobileCallback(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.username || url.password || url.search || url.hash) return false;
    if (url.protocol === "plate-os-mobile:") return url.hostname === "mobile-auth-return" && !url.pathname;
    return (url.protocol === "exp:" || url.protocol === "exps:") &&
      url.hostname.endsWith(".expo.worf.replit.dev") && url.pathname === "/--/mobile-auth-return";
  } catch { return false; }
}

export default function MobileAuth() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const params = new URLSearchParams(window.location.search);
  const challenge = params.get("challenge") ?? "";
  const state = params.get("state") ?? "";
  const callback = params.get("callback") ?? "";
  const valid = /^[A-Za-z0-9_-]{43}$/.test(challenge) && /^[a-f0-9-]{36}$/.test(state) && validMobileCallback(callback);
  async function approve() {
    if (!valid || busy) return;
    setBusy(true);
    setError("");
    try {
      const token = await getToken();
      if (!token) throw new Error("Please sign in again.");
      const { code } = await apiFetch<{ code: string }>("/mobile-auth/approve", {
        method: "POST", authToken: token, body: JSON.stringify({ challenge }),
      });
      const target = new URL(callback);
      target.searchParams.set("code", code);
      target.searchParams.set("state", state);
      window.location.assign(target.href);
    } catch {
      setError("Could not continue in the app. Please restart app sign-in.");
      setBusy(false);
    }
  }
  return (
    <main className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-6 p-6">
      <h1 className="text-2xl font-semibold">Continue in Plate OS</h1>
      {!valid ? <p role="alert">This app sign-in link is invalid. Start again from the app.</p>
        : !isLoaded ? <p>Loading secure sign-in…</p>
        : !isSignedIn ? <SignIn routing="hash" forceRedirectUrl={`${window.location.origin}${window.location.pathname}${window.location.search}`} signUpForceRedirectUrl={`${window.location.origin}${window.location.pathname}${window.location.search}`} />
        : <>
          <p>Continue as {user?.primaryEmailAddress?.emailAddress ?? "your signed-in account"}?</p>
          <p className="max-w-md text-center text-muted-foreground">
            Only continue if you started this request in your Plate OS app. This grants that app access to this account.
          </p>
          <button className="rounded-xl bg-primary px-6 py-3 text-primary-foreground" disabled={busy} onClick={() => void approve()}>
            {busy ? "Returning to the app…" : "Continue in my app"}
          </button>
          {error ? <p role="alert">{error}</p> : null}
        </>}
    </main>
  );
}